/**
 * Paper Trading Hook
 * 
 * Custom hook for managing paper trading state and operations
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { paperTradingApi } from '../services/paperTradingApi';
import { requestCache } from '@/utils/cache';
import { toast } from '@/components/ui/use-toast';

interface PaperTradingState {
  isPaperMode: boolean;
  isLoading: boolean;
  account: any | null;
  balance: number;
  error: string | null;
  resetInfo: {
    todaysResets: number;
    resetsRemaining: number;
    maxResetsPerDay: number;
    canResetToday: boolean;
  } | null;
}

export const usePaperTrading = () => {
  // Check localStorage for cached paper mode to prevent flash
  const getCachedPaperMode = () => {
    try {
      const cached = localStorage.getItem('paperTradingMode');
      return cached ? JSON.parse(cached) : false;
    } catch {
      return false;
    }
  };

  const [state, setState] = useState<PaperTradingState>({
    isPaperMode: getCachedPaperMode(), // Use cached value to prevent flash
    isLoading: true,
    account: null,
    balance: 0,
    error: null,
    resetInfo: null
  });

  // Load initial trading mode
  const loadTradingMode = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));
      const modeData = await paperTradingApi.getTradingMode();

      // Cache the mode to prevent flash on next load
      localStorage.setItem('paperTradingMode', JSON.stringify(modeData.paper_trading_enabled));

      setState(prev => ({
        ...prev,
        isPaperMode: modeData.paper_trading_enabled,
        isLoading: false
      }));

      // If in paper mode, load account data
      if (modeData.paper_trading_enabled) {
        await loadPaperAccount();
      }
    } catch (error) {
      console.error('Error loading trading mode:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load trading mode',
        isLoading: false
      }));
    }
  }, []);

  // Load paper trading account data
  const loadPaperAccount = useCallback(async (forceRefresh: boolean = false) => {
    try {
      console.log('[usePaperTrading] Loading paper account data...');

      const [accountData, balanceData] = await Promise.all([
        paperTradingApi.getPaperAccount({ forceRefresh }),
        paperTradingApi.getPaperBalance({ forceRefresh })
      ]);

      console.log('[usePaperTrading] Paper account data loaded:', {
        account: accountData.account,
        balance: balanceData.balance
      });

      // Use pure virtual balance for Virtual Balance display (no unrealized P&L)
      // The Virtual Balance should show only the actual virtual_balance from database
      const pureVirtualBalance = balanceData.balance;

      const resetInfoFromApi = (accountData as any)?.reset_info as {
        todays_resets?: number;
        resets_remaining?: number;
        max_resets_per_day?: number;
        can_reset_today?: boolean;
      } | undefined;

      const todaysResets = Number.isFinite(resetInfoFromApi?.todays_resets)
        ? Number(resetInfoFromApi?.todays_resets)
        : 0;
      const maxResetsPerDay = Number.isFinite(resetInfoFromApi?.max_resets_per_day)
        ? Number(resetInfoFromApi?.max_resets_per_day)
        : 3;
      const resetsRemaining = Number.isFinite(resetInfoFromApi?.resets_remaining)
        ? Number(resetInfoFromApi?.resets_remaining)
        : Math.max(0, maxResetsPerDay - todaysResets);
      const canResetToday = typeof resetInfoFromApi?.can_reset_today === 'boolean'
        ? resetInfoFromApi.can_reset_today
        : resetsRemaining > 0;

      setState(prev => ({
        ...prev,
        account: accountData.account,
        balance: pureVirtualBalance,
        resetInfo: {
          todaysResets,
          resetsRemaining,
          maxResetsPerDay,
          canResetToday,
        }
      }));
    } catch (error) {
      console.error('[usePaperTrading] Error loading paper account:', error);
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to load paper account'
      }));
    }
  }, []); 

  // Switch trading mode
  const lastSwitchTimeRef = useRef<number>(0);

  const switchTradingMode = useCallback(async (paperMode: boolean) => {
    // Prevent concurrent switches
    if (state.isLoading) {
      toast({ title: 'Please wait', description: 'Switch in progress', variant: 'default' });
      return;
    }

    // Record the attempt time (debounce handled at API layer via cachedPost)
    lastSwitchTimeRef.current = Date.now();

    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const result = await paperTradingApi.switchTradingMode(paperMode);

      if (result.success) {
        requestCache.invalidate('/api/paper-trading/*');

        // Cache the new mode
        localStorage.setItem('paperTradingMode', JSON.stringify(paperMode));

        if (paperMode) {
          // Load paper account data first, then set paper mode
          await loadPaperAccount();
          setState(prev => ({ ...prev, isPaperMode: paperMode }));

          // Automatically start paper trading signal generation
          console.log('[PaperTrading] Paper mode enabled - signal generation will start automatically');
        } else {
          // Clear paper account data and set live mode
          setState(prev => ({ ...prev, isPaperMode: paperMode, account: null, balance: 0 }));
        }

        // Show single success toast only
        toast({
          title: 'Trading Mode Switched',
          description: `Successfully switched to ${paperMode ? 'Paper Trading' : 'Live Trading'} mode`,
          variant: 'default'
        });
      }
    } catch (error) {
      console.error('Error switching trading mode:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to switch trading mode';

      setState(prev => ({ ...prev, error: errorMessage }));

      // Allow immediate retry after an error
      lastSwitchTimeRef.current = 0;

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [state.isLoading, loadPaperAccount]);

  // Reset paper trading account
  const resetPaperAccount = useCallback(async (newBalance?: number) => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const result = await paperTradingApi.resetPaperAccount(newBalance);

      if (result.success) {
        requestCache.invalidate('/api/paper-trading/*');

        // Reload account data to get fresh state
        await loadPaperAccount(true);

        // Force a complete refresh by updating the balance immediately
        setState(prev => ({
          ...prev,
          balance: result.new_balance,
          account: {
            ...prev.account,
            virtual_balance: result.new_balance,
            total_pnl: 0,
            total_trades_count: 0,
            winning_trades_count: 0,
            losing_trades_count: 0,
            win_rate: 0
          } as any
        }));

        toast({
          title: "Account Reset",
          description: `Paper trading account reset to $${result.new_balance.toLocaleString()}`,
          variant: "default"
        });

        // Trigger a global refresh event for all paper trading components
        window.dispatchEvent(new CustomEvent('paperTradingReset', {
          detail: { newBalance: result.new_balance }
        }));
      }
    } catch (error) {
      console.error('Error resetting paper account:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to reset paper account';

      setState(prev => ({ ...prev, error: errorMessage }));

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setState(prev => ({ ...prev, isLoading: false }));
    }
  }, [loadPaperAccount]);

  // Refresh paper trading data
  const refreshData = useCallback(async () => {
    console.log('[usePaperTrading] Refreshing data, isPaperMode:', state.isPaperMode);
    if (state.isPaperMode) {
      requestCache.invalidate('/api/paper-trading/*');
      await loadPaperAccount(true);
    }
  }, [state.isPaperMode, loadPaperAccount]); 

  // Load initial data on mount
  useEffect(() => {
    loadTradingMode();
  }, [loadTradingMode]);

  // Update balance in real-time (called from SSE updates)
  const updateBalance = useCallback((newBalance: number) => {
    setState(prev => ({
      ...prev,
      balance: newBalance
    }));
  }, []);

  return {
    // State
    isPaperMode: state.isPaperMode,
    isModeResolved: !state.isLoading,
    isLoading: state.isLoading,
    account: state.account,
    balance: state.balance,
    error: state.error,
    resetInfo: state.resetInfo,

    // Actions
    switchTradingMode,
    resetPaperAccount,
    refreshData,
    loadTradingMode,
    updateBalance
  };
};

export default usePaperTrading;
